import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {getSamplingRecords, createSamplingRecord, updateSamplingRecord} from "../../api/pond";
import {useTranslation} from "react-i18next";

interface SamplingTabProps {
  pondId: string;
  cycleId?: string;
  isCycleActive?: boolean;
}

const SamplingTab: React.FC<SamplingTabProps> = ({pondId, cycleId, isCycleActive = true}) => {
  const {t} = useTranslation();
  const [samplingRecords, setSamplingRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [formData, setFormData] = useState({
    sampledAt: "",
    averageWeight: 0,
    remark: "",
  });

  useEffect(() => {
    fetchSamplingRecords();
  }, [pondId, cycleId]);

  const fetchSamplingRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSamplingRecords(pondId, cycleId);
      setSamplingRecords(data.data || data);
    } catch (err) {
      setError("Failed to load sampling records");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (record?: any) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        sampledAt: record.sampledAt ? new Date(record.sampledAt).toISOString().slice(0, 16) : "",
        averageWeight: record.averageWeight,
        remark: record.remark || "",
      });
    } else {
      setEditingRecord(null);
      setFormData({
        sampledAt: new Date().toISOString().slice(0, 16),
        averageWeight: 0,
        remark: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  const handleSave = async () => {
    try {
      // Ensure averageWeight is a number
      const dataToSend = {
        ...formData,
        averageWeight: Number(formData.averageWeight),
      };

      console.log("Sending data:", dataToSend);

      if (editingRecord) {
        await updateSamplingRecord(editingRecord.id, dataToSend);
      } else {
        await createSamplingRecord(pondId, dataToSend);
      }
      handleCloseModal();
      fetchSamplingRecords();
    } catch (err) {
      console.error("Error saving sampling record:", err);
      setError("Failed to save sampling record");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{t("Sampling Records")}</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenModal()} disabled={!isCycleActive}>
          {t("Add Sampling Record")}
        </Button>
      </Box>
      {!isCycleActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("Viewing closed cycle data. Editing is disabled.")}
        </Alert>
      )}
      <TableContainer
        component={Paper}
        sx={{
          overflowX: "auto",
          maxWidth: "100%",
          borderRadius: 2,
          bgcolor: {xs: "#fafbfc", sm: "background.paper"},
          boxShadow: {xs: 0, sm: 1},
          mt: 1,
        }}>
        <Table
          size="small"
          sx={{
            minWidth: 600,
            "& th, & td": {
              fontSize: {xs: "0.85rem", sm: "1rem"},
              px: {xs: 0.5, sm: 2},
              py: {xs: 0.5, sm: 1},
              wordBreak: "break-word",
              whiteSpace: {xs: "pre-line", sm: "nowrap"},
            },
            "& th": {whiteSpace: {xs: "pre-line", sm: "nowrap"}},
          }}>
          <TableHead>
            <TableRow>
              <TableCell>{t("Date")}</TableCell>
              <TableCell>{t("Sample Size")}</TableCell>
              <TableCell>{t("Average Weight")}</TableCell>
              <TableCell>{t("Total Weight")}</TableCell>
              <TableCell>{t("Remark")}</TableCell>
              <TableCell>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {samplingRecords.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.sampledAt ? new Date(record.sampledAt).toLocaleDateString() : "-"}</TableCell>
                <TableCell>{record.sampleSize}</TableCell>
                <TableCell>{record.averageWeight}</TableCell>
                <TableCell>{record.totalWeight}</TableCell>
                <TableCell sx={{maxWidth: 150, wordWrap: "break-word"}}>{record.remark || ""}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => handleOpenModal(record)} disabled={!isCycleActive}>
                    {t("Edit")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRecord ? t("Edit Sampling Record") : t("Add Sampling Record")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t("Sampled At")}
              type="datetime-local"
              value={formData.sampledAt}
              onChange={(e) => setFormData({...formData, sampledAt: e.target.value})}
              InputLabelProps={{shrink: true}}
            />
            <TextField
              label={t("Average Weight (g)")}
              type="number"
              value={formData.averageWeight}
              onChange={(e) => setFormData({...formData, averageWeight: parseFloat(e.target.value)})}
            />
            <TextField
              label={t("Remark")}
              multiline
              rows={3}
              value={formData.remark}
              onChange={(e) => setFormData({...formData, remark: e.target.value})}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t("Cancel")}</Button>
          <Button onClick={handleSave} variant="contained">
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SamplingTab;
